// Every tuning constant the independent review flagged as "lives in TypeScript, a planner cannot see or
// argue with" (docs/reviews/2026-09-21-independent-review.md section B), moved to data. `data/rules.json`
// holds three named presets; `loadRules` resolves one (with optional per-field overrides, for the
// Instructor panel's sliders) into a `Rules` object that combat/logistics/orders/detection/redcell read
// instead of their old inline constants. See docs/planning/2026-09-21-pitches.md pitch 10.
import type { Posture, UnitType } from './types';
// eslint-disable-next-line @typescript-eslint/no-var-requires
import rulesJson from '../../data/rules.json';

export type RulesPreset = 'training' | 'realistic' | 'ukraine-2024';

export const RULES_PRESET_NAMES: RulesPreset[] = ['training', 'realistic', 'ukraine-2024'];

export interface RulesFireMission {
  /** Area-effect radius in meters: every unit (either side - friendly fire is real) within this of the
   * dispersed impact point takes the volley's damage. */
  radiusM: number;
  /** Volleys per fire mission before the order completes. */
  volleys: number;
  ammoCostPerVolley: number;
  /** Below this ammo, the firing unit skips its scheduled volley this tick. */
  minAmmoToFire: number;
  /** missionDmg = firer's firepower x this. */
  damageMultiplier: number;
  strengthLossFraction: number;
  suppressionGainFraction: number;
  /** The firing side needs a contact on something within this many meters of the target cell. */
  observerRadiusM: number;
  /** ...and that contact's lastSeen must be within this many ticks of now. */
  observerFreshTicks: number;
  /** The impact point is offset from the called target cell by a seeded random up to this many meters. */
  dispersionMaxM: number;
  /** Minutes between one artillery unit's fire-mission considerations (red cell only). */
  cooldownMin: number;
}

export interface RulesC2 {
  /** Base transmission-delay minutes for company-type ground maneuver units (armor/mech/inf/recon). */
  companyBaseMin: number;
  /** Base minutes for everything else. */
  otherBaseMin: number;
  ewFactor: { none: number; moderate: number; heavy: number };
  /** Multiplier when the ordered unit is in contact. */
  contactFactor: number;
  /** Multiplier when the side has no living headquarters. */
  noHqFactor: number;
  /** Extra multiplier the "degraded" C2 dial applies on top of "realistic". */
  degradedFactor: number;
}

export interface RulesDetection {
  movingMult: number;
  staticMult: number;
  todNight: number;
  todDawn: number;
  todDay: number;
  concealmentDivisor: number;
}

export interface RulesLogistics {
  fuelLowThreshold: number;
  ammoLowThreshold: number;
  forceFuelLowThreshold: number;
  culminationThreshold: number;
  resupplyRadiusM: number;
  resupplyFuelPerMin: number;
  resupplyAmmoPerMin: number;
}

export interface Rules {
  /** The overall direct-fire damage scalar (spec calls it "the 0.05 lethality scalar"). */
  lethalityScalar: number;
  /** How a direct-fire hit splits between strength loss and suppression gain. */
  damageSplit: { loss: number; suppression: number };
  /** Suppression gained is (dmg x damageSplit.suppression x this). */
  suppressionGainMultiplier: number;
  /** Suppression lost per tick when the unit was not fired on this minute. */
  suppressionDecayPerTick: number;
  pinnedThreshold: number;
  unpinnedThreshold: number;
  /** Morale lost per point of strength lost this tick. */
  moraleLossPerDamagePoint: number;
  moraleSuppressionPenalty: number;
  moraleIsolationPenalty: number;
  moraleRecoveryPerTick: number;
  /** Morale below this: the unit breaks and withdraws. */
  breakThreshold: number;
  /** Strength at or below this: the unit is destroyed. */
  destroyThreshold: number;
  armorMult: Record<UnitType, Record<UnitType, number>>;
  postureIncomingMult: Record<Posture, number>;
  /** terrainIn = 1 - cover / this, for direct fire. */
  terrainDivisor: number;
  /** Below this ammo, a direct-firing unit's damage is halved. */
  ammoLowThresholdCombat: number;
  ammoLowFireFactor: number;
  fireMission: RulesFireMission;
  uasLossByEw: { none: number; moderate: number; heavy: number };
  c2: RulesC2;
  /** Report latency (contact/friendly-position arrival at headquarters) = C2 delay / this. */
  reportLatencyDivisor: number;
  detection: RulesDetection;
  logistics: RulesLogistics;
  /** Red's starting morale (blue always starts at 100 - see spec/pitch 9a "morale parity"). Defaults to
   * 100 (parity); a ruleset or instructor override may set it lower, which the OPORD situation card must
   * disclose. */
  redMoraleStart: number;
}

const PRESETS = rulesJson as unknown as Record<RulesPreset, Rules>;

/** The three named presets, verbatim from data/rules.json. */
export const RULES_PRESETS: Record<RulesPreset, Rules> = PRESETS;

/** A partial, arbitrarily-nested override of a `Rules` object (the Instructor panel's sliders build these). */
export type RulesOverrides = { [K in keyof Rules]?: Rules[K] extends object ? RulesOverrides2<Rules[K]> : Rules[K] };
// Two levels of nesting cover every leaf in the interface above (fireMission.*, c2.ewFactor.*, etc.).
type RulesOverrides2<T> = { [K in keyof T]?: T[K] extends object ? Partial<T[K]> : T[K] };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Recursively merges `overrides` onto `base`, without mutating either. Used both by `loadRules` and by
 * the Instructor panel to fold one slider's change into the overrides object already held in dials. */
export function deepMergeRules<T extends Record<string, unknown>>(base: T, overrides: Partial<T> | undefined): T {
  if (!overrides) return base;
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(overrides)) {
    const ov = (overrides as Record<string, unknown>)[key];
    const baseVal = (base as Record<string, unknown>)[key];
    out[key] = isPlainObject(ov) && isPlainObject(baseVal)
      ? deepMergeRules(baseVal as Record<string, unknown>, ov as Record<string, unknown>)
      : ov;
  }
  return out as T;
}

/** True if `overrides` actually changes anything (recursively) - drives the "(modified)" suffix in the UI. */
export function rulesOverridesNonEmpty(overrides: RulesOverrides | undefined): boolean {
  if (!overrides) return false;
  return Object.keys(overrides).length > 0;
}

/**
 * Resolves a named preset (default "realistic"), with optional overrides folded in, into a concrete
 * `Rules` object. Pure and synchronous - every module that used to read an inline constant now reads the
 * matching field of the `Rules` this returns.
 */
export function loadRules(preset: RulesPreset = 'realistic', overrides?: RulesOverrides): Rules {
  const base = PRESETS[preset] ?? PRESETS.realistic;
  return overrides ? deepMergeRules(base as unknown as Record<string, unknown>, overrides as Record<string, unknown>) as unknown as Rules : base;
}
